import { bulkApproveBodyFor } from '../../../components/OrderBulkActionsBar/bulkApprove';
import { describeOrderActionError } from '../actionErrors';

/**
 * The items array for a bulk status change.
 *
 * `approve` is what makes a PENDING/WAITLIST load movable at all: the
 * status endpoint writes nothing but the status column, so a load reaching
 * APPROVED through it would skip its warehouse, snapshots, fee conversion and
 * order totals. Those rows carry a full approval body instead — see
 * `bulkApproveBodyFor`.
 */
export const buildBulkStatusItems = (rows: any[], status: string) =>
  (rows || []).map((row: any) => ({
    orderId: row.order?.id ?? row.orderId,
    itemId: row.id,
    loadNumber: row.loadNumber,
    approve: bulkApproveBodyFor(row, status) ?? undefined,
  }));

/**
 * What to tell the admin after a partial bulk result.
 *
 * A partial failure names the first one specifically — "12 of 26 updated" with
 * no clue which 14 failed is not actionable — and the caller keeps the
 * selection so the failures can be retried.
 */
export const summarizeBulkResult = (
  succeeded: any[],
  failed: Array<{ item: { loadNumber?: string | null }; error: string }>,
  attempted: number,
  statusText: string
): { tone: 'success' | 'warn'; message: string; clearSelection: boolean } => {
  if (failed.length === 0) {
    return {
      tone: 'success',
      message: `${succeeded.length} load${succeeded.length === 1 ? '' : 's'} set to ${statusText}.`,
      clearSelection: true,
    };
  }

  const firstError = failed[0];

  return {
    tone: 'warn',
    message:
      `${succeeded.length} of ${attempted} updated. ${failed.length} failed. ` +
      `${firstError.item.loadNumber ?? 'a load'}: ${describeOrderActionError(firstError.error, 'failed')}`,
    clearSelection: false,
  };
};
